import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, FileUp, Loader2, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { accApi, accErr, money } from "./AccountsPanel";
import { API, getAuthHeader } from "@/App";

const NEW = "__new__";
const pct = (s) => `${Math.round(s * 100)}%`;

const MatchSelect = ({ value, onChange, matches, all, newLabel, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`w-full rounded border px-1.5 py-1 text-xs ${value === NEW ? "border-amber-400 bg-amber-50" : ""}`}>
    <option value={NEW}>+ Create new: {newLabel}</option>
    {matches.length > 0 && <optgroup label="Closest matches">{matches.map((m) => <option key={m.id} value={m.id}>{m.name} ({pct(m.score)})</option>)}</optgroup>}
    <optgroup label="All">{all.filter((a) => !matches.some((m) => m.id === a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>
  </select>
);

const Review = ({ company, scan, onClose, onFill }) => {
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const top = (m, cut) => (m[0] && m[0].score >= cut ? m[0].id : NEW);
  const [sup, setSup] = useState(top(scan.supplier_matches, 0.85));
  const [pick, setPick] = useState(scan.items.map((i) => top(i.matches, 0.8)));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    accApi(`/parties?company_id=${company.id}`).then((r) => setParties(r.filter((p) => p.type === "supplier"))).catch(accErr);
    accApi(`/products?company_id=${company.id}`).then(setProducts).catch(accErr);
  }, [company.id]);
  const fill = async () => {
    if (sup === NEW && !scan.supplier.name) { toast.error("Supplier name not read — pick a supplier"); return; }
    setBusy(true);
    try {
      let party_id = sup;
      if (sup === NEW) party_id = (await accApi("/parties", { method: "POST", data: { company_id: company.id, type: "supplier", ...scan.supplier, country: "India" } })).id;
      const ids = [];
      for (const [i, it] of scan.items.entries()) {
        ids.push(pick[i] !== NEW ? pick[i] : (await accApi("/products", { method: "POST", data: { company_id: company.id, name: it.name, hsn: it.hsn, gst_rate: it.gst_rate, packing: it.packing } })).id);
      }
      const made = (sup === NEW ? 1 : 0) + pick.filter((p) => p === NEW).length;
      if (made) toast.success(`Created ${sup === NEW ? "supplier + " : ""}${pick.filter((p) => p === NEW).length} new product(s)`);
      onFill({ type: "purchase", party_id, ...(/^\d{4}-\d{2}-\d{2}$/.test(scan.bill_date) ? { date: scan.bill_date } : {}), supplier_bill_no: scan.bill_no,
        notes: scan.bill_no ? `Supplier bill ${scan.bill_no}` : "",
        items: scan.items.map((it, i) => ({ product_id: ids[i], qty: it.qty, free_qty: it.free_qty || "", rate: it.rate, discount: it.discount || "", gst_rate: it.gst_rate, batch: it.batch, expiry: it.expiry, mrp: it.mrp || "" })) });
    } catch (e) { accErr(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="acc-scan-review">
        <DialogHeader><DialogTitle>Check the scanned bill · {scan.bill_no || "no bill no."} · {scan.bill_date || "no date"}</DialogTitle></DialogHeader>
        <div className="grid gap-2 rounded-lg border bg-slate-50 p-2 text-xs md:grid-cols-2">
          <div><b className="text-sm">{scan.supplier.name || "Supplier not read"}</b><div className="text-slate-500">{[scan.supplier.gstin && `GSTIN ${scan.supplier.gstin}`, scan.supplier.address].filter(Boolean).join(" · ")}</div></div>
          <MatchSelect value={sup} onChange={setSup} matches={scan.supplier_matches} all={parties} newLabel={scan.supplier.name || "—"} testid="acc-scan-supplier" />
        </div>
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead className="text-left text-slate-500"><tr><th className="py-1">On the bill</th><th className="min-w-[220px]">Product in your list</th><th>Batch</th><th>Exp</th><th className="text-right">MRP</th><th className="text-right">Qty+Free</th><th className="text-right">Rate</th><th className="text-right">Disc%</th><th className="text-right">GST%</th></tr></thead>
          <tbody>{scan.items.map((it, i) => (
            <tr key={i} className="border-t align-top" data-testid={`acc-scan-line-${i}`}>
              <td className="py-1 pr-2 font-medium">{it.name}<div className="text-[10px] text-slate-400">{[it.packing, it.hsn && `HSN ${it.hsn}`].filter(Boolean).join(" · ")}</div></td>
              <td className="pr-2"><MatchSelect value={pick[i]} onChange={(x) => setPick((p) => p.map((y, j) => (j === i ? x : y)))} matches={it.matches} all={products} newLabel={it.name} testid={`acc-scan-product-${i}`} /></td>
              <td>{it.batch || "—"}</td><td>{it.expiry || "—"}</td><td className="text-right">{it.mrp || "—"}</td>
              <td className="text-right">{it.qty}{it.free_qty ? `+${it.free_qty}` : ""}</td><td className="text-right">{it.rate}</td><td className="text-right">{it.discount || ""}</td><td className="text-right">{it.gst_rate}</td>
            </tr>))}</tbody></table></div>
        {scan.totals.grand_total > 0 && <p className="text-right text-xs text-slate-500">Bill total as printed: <b>{money(scan.totals.grand_total)}</b> (taxable {money(scan.totals.taxable)} + GST {money(scan.totals.tax)}) — compare with the form after filling.</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border px-3 py-2 text-sm">Cancel</button>
          <button disabled={busy} onClick={fill} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="acc-scan-fill">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Fill purchase bill</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const BillScanButtons = ({ company, onFill }) => {
  const cam = useRef(null);
  const up = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const go = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("company_id", company.id); fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/acc/purchase-scan`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setScan(data);
    } catch (e) { accErr(e); } finally { setBusy(false); if (cam.current) cam.current.value = ""; if (up.current) up.current.value = ""; }
  };
  const b = "inline-flex items-center gap-1 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-50";
  return (<>
    <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => go(e.target.files[0])} data-testid="acc-scan-camera-input" />
    <input ref={up} type="file" accept="application/pdf,image/*" hidden onChange={(e) => go(e.target.files[0])} data-testid="acc-scan-upload-input" />
    <button disabled={busy} onClick={() => cam.current.click()} className={b} data-testid="acc-scan-camera"><Camera className="h-4 w-4" /> Scan bill</button>
    <button disabled={busy} onClick={() => up.current.click()} className={b} data-testid="acc-scan-upload">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {busy ? "Reading bill…" : "Upload bill PDF / image"}</button>
    {busy && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><ScanLine className="h-3.5 w-3.5" /> Reading every line — 10–40 s</span>}
    {scan && <Review company={company} scan={scan} onClose={() => setScan(null)} onFill={(v) => { setScan(null); onFill(v); }} />}
  </>);
};
